/* UI-SP2-5 / UI-SP3-1 — Public landing page. First thing a visitor sees
   before logging in. Marketing-style hero + value-prop stats + two CTAs.
   "Access Platform" routes to /dashboard (auth-protected, redirects to
   /login if not signed in).

   2026-06-19 rewrite to align with `New UI Changes/index.html`:
   - Dark navy nav with title that reads "{BRAND} Audit AI"
   - Dark gradient hero with subtle grid overlay
   - Pill row: On-premise · Air-gapped · OIA Network (status badges)
   - Outlined dark Sign In / Access Platform CTAs (no more orange)
   - Stats label "AI models loaded" (was "Modules ready")
   - Modules section title "Two specialised assessment workflows"
   - Module cards carry feature lists + "Start DD/SPA →" inline CTAs
   - "Built for OIA Infrastructure" trust section
   - Footer matches design: product name · deployment · sign-in link
   - REMOVED the "One Project · Three audit stages" section — the
     design doesn't include it on the public landing page.
*/
import { useNavigate } from 'react-router-dom';
import { BRAND_NAME, BRAND_LOGO_SRC, BRAND_CODE, PRODUCT_SUBLABEL } from '../config/branding';
import { DD_SECTION_COUNT, SPA_SECTION_COUNT } from '../config/reportSections';
import { useAuth } from '../context/AuthContext';

interface LandingStat {
  value: string;
  label: string;
}

// Sprint 5 client feedback 2026-07-03 — dropped the hardcoded
// "1,284 Reports generated" stat. It's a fabricated counter that
// never reflected real activity, and hardcoding a made-up number
// on the landing page erodes trust. The remaining three stats are
// deployment-configuration facts (max doc size / models loaded /
// on-premise) that are stable across tenants.
const STATS: LandingStat[] = [
  { value: '50 MB', label: 'Max document size' },
  { value: '4', label: 'AI models loaded' },
  { value: '100%', label: 'On-premise processing' },
];

const ARROW_ICON = (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const CHECK_ICON = (
  <svg
    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const accessLabel = isAuthenticated ? 'Open Dashboard' : 'Access Platform';
  const productTitle = `${BRAND_NAME} ${PRODUCT_SUBLABEL}`;

  return (
    <div className="landing-root">
      <header className="landing-top">
        <div className="landing-brand">
          {BRAND_LOGO_SRC ? (
            <img className="landing-brand-logo" src={BRAND_LOGO_SRC} alt={`${BRAND_NAME} logo`} />
          ) : (
            <div className="landing-brand-tile">{BRAND_CODE}</div>
          )}
          <span className="landing-brand-name">{productTitle}</span>
        </div>
        <nav className="landing-top-nav">
          <a className="landing-nav-link" href="#modules">Modules</a>
          <button
            type="button"
            className="landing-signin-btn"
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
          >
            {isAuthenticated ? 'Dashboard' : 'Sign in'}
            {ARROW_ICON}
          </button>
        </nav>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-pill-row">
            <span className="landing-pill landing-pill-on">
              <span className="landing-status-dot" />
              On-premise
            </span>
            <span className="landing-pill">Air-gapped</span>
            <span className="landing-pill">OIA Network</span>
          </div>
          <h1 className="landing-headline">
            Intelligent Investment Assessment,<br />
            On Your Infrastructure
          </h1>
          <p className="landing-sub">
            AI-powered due diligence and SPA legal review. {DD_SECTION_COUNT}-section DD
            reports and {SPA_SECTION_COUNT}-section SPA analysis, benchmarked against OIA
            portfolio standards — all within your network perimeter.
          </p>
          <div className="landing-cta-row">
            <button
              type="button"
              className="landing-cta-primary"
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
            >
              {accessLabel}
              {ARROW_ICON}
            </button>
            <a className="landing-cta-secondary" href="#modules">
              Explore Modules
            </a>
          </div>
        </div>
      </section>

      <section className="landing-stats" aria-label="Platform statistics">
        {STATS.map((s) => (
          <div className="landing-stat" key={s.label}>
            <div className="landing-stat-value">{s.value}</div>
            <div className="landing-stat-label">{s.label}</div>
          </div>
        ))}
      </section>

      <section id="modules" className="landing-modules">
        <p className="landing-modules-eyebrow">Platform Modules</p>
        <h2 className="landing-modules-title">Two specialised assessment workflows</h2>
        <div className="landing-module-grid">
          <article className="landing-module-card landing-module-card-dd">
            <span className="landing-module-chip landing-module-chip-dd">DD Module</span>
            <h3 className="landing-module-title">Due Diligence Assessment</h3>
            <p className="landing-module-desc">
              Upload investment memos, financial DD reports, techno-commercial
              DD reports, and IC resolutions. Extract key metrics, validate
              calculations, and benchmark against OIA portfolio thresholds.
            </p>
            <ul className="landing-module-features">
              <li><span className="feat-check">{CHECK_ICON}</span>{DD_SECTION_COUNT}-section comprehensive report</li>
              <li><span className="feat-check">{CHECK_ICON}</span>IRR / XIRR extraction &amp; benchmark</li>
              <li><span className="feat-check">{CHECK_ICON}</span>Calculation verification &amp; risk flags</li>
              <li><span className="feat-check">{CHECK_ICON}</span>PDF, DOCX, XLSX — up to 50 MB</li>
            </ul>
            <button
              type="button"
              className="landing-module-cta landing-module-cta-dd"
              onClick={() => navigate(isAuthenticated ? '/projects/new' : '/login')}
            >
              Start DD Assessment {ARROW_ICON}
            </button>
          </article>

          <article className="landing-module-card landing-module-card-spa">
            <span className="landing-module-chip landing-module-chip-spa">SPA Module</span>
            <h3 className="landing-module-title">SPA Legal Review</h3>
            <p className="landing-module-desc">
              Upload share purchase agreements, shareholders agreements, or
              escrow documents. Detect mandatory clauses, analyse investor
              protections, and identify legal issues.
            </p>
            <ul className="landing-module-features">
              <li><span className="feat-check">{CHECK_ICON}</span>{SPA_SECTION_COUNT}-section legal analysis report</li>
              <li><span className="feat-check">{CHECK_ICON}</span>Mandatory clause detection (10 sections)</li>
              <li><span className="feat-check">{CHECK_ICON}</span>Investor protection analysis</li>
              <li><span className="feat-check">{CHECK_ICON}</span>External counsel sign-off workflow</li>
            </ul>
            <button
              type="button"
              className="landing-module-cta landing-module-cta-spa"
              onClick={() => navigate(isAuthenticated ? '/projects/new' : '/login')}
            >
              Start SPA Review {ARROW_ICON}
            </button>
          </article>
        </div>
      </section>

      <section className="landing-trust">
        <div className="landing-trust-intro">
          <h2 className="landing-trust-title">Built for OIA Infrastructure</h2>
          <p className="landing-trust-sub">
            All processing happens on your on-premise servers — no data
            leaves the OIA network.
          </p>
        </div>
        <div className="landing-trust-grid">
          <div className="landing-trust-item">
            <div className="landing-trust-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <div className="landing-trust-item-title">Air-gapped Processing</div>
            <div className="landing-trust-item-desc">
              No document data leaves your network. All AI inference runs
              locally on OIA servers.
            </div>
          </div>
          <div className="landing-trust-item">
            <div className="landing-trust-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="16" rx="2" />
                <rect x="9" y="9" width="6" height="6" />
                <line x1="9" y1="1" x2="9" y2="4" />
                <line x1="15" y1="1" x2="15" y2="4" />
                <line x1="9" y1="20" x2="9" y2="23" />
                <line x1="15" y1="20" x2="15" y2="23" />
                <line x1="20" y1="9" x2="23" y2="9" />
                <line x1="20" y1="14" x2="23" y2="14" />
                <line x1="1" y1="9" x2="4" y2="9" />
                <line x1="1" y1="14" x2="4" y2="14" />
              </svg>
            </div>
            <div className="landing-trust-item-title">On-premise AI Models</div>
            <div className="landing-trust-item-desc">
              4 specialised models for extraction, classification,
              benchmarking, and report generation.
            </div>
          </div>
          <div className="landing-trust-item">
            <div className="landing-trust-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="landing-trust-item-title">Full Audit Trail</div>
            <div className="landing-trust-item-desc">
              Every data point is traced back to source document page,
              section, and version.
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <span className="t-xs t-muted">
          {productTitle} v1.0
          {' · '}
          On-premise deployment
          {' · '}
          <button
            type="button"
            className="landing-footer-signin"
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
          >
            {isAuthenticated ? 'Dashboard' : 'Sign in'} →
          </button>
        </span>
      </footer>
    </div>
  );
}
